import {
	ChangeEvent,
	type PointerEvent as ReactPointerEvent,
	useEffect,
	useRef,
	useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import type { CampoAssinaturaDigitalProps, TipoModoAssinatura } from '../types';

const larguraQuadroAssinatura = 900;
const alturaQuadroAssinatura = 260;

const fonteAssinatura =
	'"Brush Script MT", "Segoe Script", "Lucida Handwriting", cursive';

const gerarAssinaturaDigitada = (textoAssinatura: string) => {
	if (!textoAssinatura.trim()) return '';

	const canvasAssinatura = document.createElement('canvas');
	canvasAssinatura.width = larguraQuadroAssinatura;
	canvasAssinatura.height = alturaQuadroAssinatura;

	const contextoAssinatura = canvasAssinatura.getContext('2d');
	if (!contextoAssinatura) return '';

	contextoAssinatura.fillStyle = '#ffffff';
	contextoAssinatura.fillRect(
		0,
		0,
		larguraQuadroAssinatura,
		alturaQuadroAssinatura
	);
	contextoAssinatura.fillStyle = '#111827';
	contextoAssinatura.font = `italic 76px ${fonteAssinatura}`;
	contextoAssinatura.textAlign = 'center';
	contextoAssinatura.textBaseline = 'middle';
	contextoAssinatura.fillText(
		textoAssinatura,
		larguraQuadroAssinatura / 2,
		alturaQuadroAssinatura / 2,
		larguraQuadroAssinatura - 96
	);

	return canvasAssinatura.toDataURL('image/png');
};

const CampoAssinaturaDigital = ({
	titulo,
	obrigatoria,
	valorAssinatura,
	aoAlterarAssinatura,
}: CampoAssinaturaDigitalProps) => {
	const { t } = useTranslation();
	const canvasAssinaturaRef = useRef<HTMLCanvasElement | null>(null);
	const desenhandoAssinaturaRef = useRef(false);
	const ultimaAssinaturaRenderizadaRef = useRef<string>();

	const [modoAssinatura, setModoAssinatura] = useState<TipoModoAssinatura>('desenho');
	const [textoAssinatura, setTextoAssinatura] = useState('');

	const prepararQuadroDesenho = () => {
		const canvasAssinatura = canvasAssinaturaRef.current;
		if (!canvasAssinatura) return;

		canvasAssinatura.width = larguraQuadroAssinatura;
		canvasAssinatura.height = alturaQuadroAssinatura;

		const contextoAssinatura = canvasAssinatura.getContext('2d');
		if (!contextoAssinatura) return;

		contextoAssinatura.fillStyle = '#ffffff';
		contextoAssinatura.fillRect(
			0,
			0,
			larguraQuadroAssinatura,
			alturaQuadroAssinatura
		);
		contextoAssinatura.strokeStyle = '#111827';
		contextoAssinatura.lineWidth = 4;
		contextoAssinatura.lineCap = 'round';
		contextoAssinatura.lineJoin = 'round';
	};

	useEffect(() => {
		if (modoAssinatura !== 'desenho') return;
		if (ultimaAssinaturaRenderizadaRef.current === valorAssinatura) return;

		let carregamentoAtivo = true;
		prepararQuadroDesenho();

		if (!valorAssinatura || !valorAssinatura.startsWith('data:image')) {
			ultimaAssinaturaRenderizadaRef.current = valorAssinatura;
			return;
		}

		const canvasAssinatura = canvasAssinaturaRef.current;
		const contextoAssinatura = canvasAssinatura?.getContext('2d');
		if (!canvasAssinatura || !contextoAssinatura) return;

		const imagemAssinatura = new Image();

		imagemAssinatura.onload = () => {
			if (!carregamentoAtivo) return;

			contextoAssinatura.drawImage(
				imagemAssinatura,
				0,
				0,
				larguraQuadroAssinatura,
				alturaQuadroAssinatura
			);
			ultimaAssinaturaRenderizadaRef.current = valorAssinatura;
		};
		imagemAssinatura.src = valorAssinatura;

		return () => {
			carregamentoAtivo = false;
		};
	}, [modoAssinatura, valorAssinatura]);

	const obterPosicaoAssinatura = (
		event: ReactPointerEvent<HTMLCanvasElement>
	) => {
		const canvasAssinatura = canvasAssinaturaRef.current;
		if (!canvasAssinatura) return { x: 0, y: 0 };

		const dimensoesCanvas = canvasAssinatura.getBoundingClientRect();

		return {
			x:
				((event.clientX - dimensoesCanvas.left) / dimensoesCanvas.width) *
				larguraQuadroAssinatura,
			y:
				((event.clientY - dimensoesCanvas.top) / dimensoesCanvas.height) *
				alturaQuadroAssinatura,
		};
	};

	const iniciarDesenhoAssinatura = (
		event: ReactPointerEvent<HTMLCanvasElement>
	) => {
		if (valorAssinatura && !valorAssinatura.startsWith('data:image')) {
			prepararQuadroDesenho();
			ultimaAssinaturaRenderizadaRef.current = '';
			aoAlterarAssinatura('');
		}

		const contextoAssinatura =
			canvasAssinaturaRef.current?.getContext('2d');
		if (!contextoAssinatura) return;

		const posicaoAssinatura = obterPosicaoAssinatura(event);
		desenhandoAssinaturaRef.current = true;
		contextoAssinatura.beginPath();
		contextoAssinatura.moveTo(posicaoAssinatura.x, posicaoAssinatura.y);
		event.currentTarget.setPointerCapture(event.pointerId);
	};

	const desenharAssinatura = (
		event: ReactPointerEvent<HTMLCanvasElement>
	) => {
		if (!desenhandoAssinaturaRef.current) return;

		const contextoAssinatura =
			canvasAssinaturaRef.current?.getContext('2d');
		if (!contextoAssinatura) return;

		const posicaoAssinatura = obterPosicaoAssinatura(event);
		contextoAssinatura.lineTo(posicaoAssinatura.x, posicaoAssinatura.y);
		contextoAssinatura.stroke();
	};

	const finalizarDesenhoAssinatura = () => {
		if (!desenhandoAssinaturaRef.current) return;

		desenhandoAssinaturaRef.current = false;

		try {
			const assinaturaAtual = canvasAssinaturaRef.current?.toDataURL('image/png');
			ultimaAssinaturaRenderizadaRef.current = assinaturaAtual || '';
			aoAlterarAssinatura(assinaturaAtual || '');
		} catch {
			prepararQuadroDesenho();
			ultimaAssinaturaRenderizadaRef.current = '';
			aoAlterarAssinatura('');
		}
	};

	const lidarComTextoAssinatura = (
		event: ChangeEvent<HTMLInputElement>
	) => {
		const textoAtual = event.target.value;
		const assinaturaGerada = gerarAssinaturaDigitada(textoAtual);
		setTextoAssinatura(textoAtual);
		ultimaAssinaturaRenderizadaRef.current = assinaturaGerada;
		aoAlterarAssinatura(assinaturaGerada);
	};

	const alterarModoAssinatura = (novoModoAssinatura: TipoModoAssinatura) => {
		setModoAssinatura(novoModoAssinatura);
		setTextoAssinatura('');
		if (novoModoAssinatura === 'desenho') prepararQuadroDesenho();
		ultimaAssinaturaRenderizadaRef.current = '';
		aoAlterarAssinatura('');
	};

	const limparAssinatura = () => {
		setTextoAssinatura('');
		ultimaAssinaturaRenderizadaRef.current = '';
		aoAlterarAssinatura('');
		prepararQuadroDesenho();
	};

	const assinaturaRemotaVisivel = Boolean(
		modoAssinatura === 'desenho' &&
		valorAssinatura &&
		!valorAssinatura.startsWith('data:image')
	);

	return (
		<div>
			<p className="mb-2 text-sm font-semibold">
				{obrigatoria && <span className="text-[#dc2626]">*</span>}{' '}
				{titulo}
			</p>

			<div className="mb-3 inline-flex overflow-hidden rounded-md border border-[#cbd5e1] bg-[#f8fafc]">
				<button
					className={`px-4 py-2 text-sm font-semibold transition-colors ${modoAssinatura === 'desenho'
						? 'bg-[#2f70f0] text-white'
						: 'text-[#475569] hover:bg-[#e2e8f0]'
						}`}
					onClick={() => alterarModoAssinatura('desenho')}
					type="button"
				>
					{t('fluxo_unico.signature.draw')}
				</button>
				<button
					className={`px-4 py-2 text-sm font-semibold transition-colors ${modoAssinatura === 'texto'
						? 'bg-[#2f70f0] text-white'
						: 'text-[#475569] hover:bg-[#e2e8f0]'
						}`}
					onClick={() => alterarModoAssinatura('texto')}
					type="button"
				>
					{t('fluxo_unico.signature.type')}
				</button>
			</div>

			{modoAssinatura === 'texto' && (
				<input
					className="mb-3 h-11 w-full rounded-md border border-[#cbd5e1] bg-white px-3 text-[#1f2937] outline-none focus:border-[#2f70f0] focus:ring-2 focus:ring-[#dbeafe]"
					onChange={lidarComTextoAssinatura}
					placeholder={t('fluxo_unico.signature.type_name_placeholder')}
					value={textoAssinatura}
				/>
			)}

			<div className="relative h-36 overflow-hidden rounded-sm border border-[#cbd5e1] bg-white">
				{modoAssinatura === 'desenho' ? (
					<>
						<canvas
							className="relative z-0 h-full w-full touch-none cursor-crosshair"
							height={alturaQuadroAssinatura}
							onPointerCancel={finalizarDesenhoAssinatura}
							onPointerDown={iniciarDesenhoAssinatura}
							onPointerLeave={finalizarDesenhoAssinatura}
							onPointerMove={desenharAssinatura}
							onPointerUp={finalizarDesenhoAssinatura}
							ref={canvasAssinaturaRef}
							width={larguraQuadroAssinatura}
						/>
						{assinaturaRemotaVisivel && (
							<img
								alt={titulo}
								className="pointer-events-none absolute inset-0 z-10 h-full w-full object-contain"
								src={valorAssinatura}
							/>
						)}
					</>
				) : (
					<div
						className="flex h-full w-full items-center justify-center px-6 text-center text-5xl italic text-[#111827]"
						style={{ fontFamily: fonteAssinatura }}
					>
						{textoAssinatura}
					</div>
				)}
			</div>

			<button
				className="mt-3 h-10 w-full rounded-md border border-[#cbd5e1] bg-[#f8fafc] text-sm text-[#475569] transition-colors hover:border-[#94a3b8] hover:bg-[#e2e8f0]"
				onClick={limparAssinatura}
				type="button"
			>
				🗑 {t('fluxo_unico.signature.clear')}
			</button>
		</div>
	);
};

export default CampoAssinaturaDigital;
